import { Box, Spinner, Stack, Text } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { ImFileEmpty } from 'react-icons/im';
import { ISelectiveProcess } from '../../../../services/hooks/SelectiveProcess/useRequestSelectiveProcess';
import CardSelectiveProcess from './cardSelectiveProcess';
import FilterSelectiveProcess from './filterSelectiveProcess';
import { FiRefreshCw } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import AlertMobile from '../../../../components/Mobile/alert';

interface IOwnProps {
  emailSuport?: string;
  setSelectedButton: React.Dispatch<React.SetStateAction<string | undefined>>;
  setFinalSearch: React.Dispatch<React.SetStateAction<string>>;
  finalSearch: string;
  isLoading: boolean;
  selectedButton?: string;
  data?: ISelectiveProcess[];
}

const MobilePage: React.FC<IOwnProps> = ({
  emailSuport,
  data,
  isLoading,
  selectedButton,
  finalSearch,
  setSelectedButton,
  setFinalSearch,
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const maxPull = 80;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    if (startY.current === null) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff, maxPull));
    }
  };

  async function handleTouchEnd() {
    if (pullDistance >= maxPull) {
      setIsRefreshing(true);
      await queryClient.refetchQueries({
        queryKey: ['selective_process', selectedButton || null, finalSearch],
        exact: true,
      });
      setIsRefreshing(false);
    }
    setPullDistance(0);
    startY.current = null;
  }

  let labelFilter = 'TODOS';

  if (selectedButton) {
    labelFilter = selectedButton.toUpperCase();
    if (!labelFilter.includes('ANDAMENTO') && !labelFilter.includes('ABERTAS')) labelFilter += 'S';
  }

  return (
    <Stack
      h="full"
      mx={2}
      overflowX="hidden"
      ref={containerRef}
      overflowY="auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      position="relative"
      bg="gray.50"
      borderRadius="md"
      boxShadow="md"
    >
      <Box
        position="absolute"
        top={-50 + pullDistance}
        left="50%"
        transform="translateX(-50%)"
        transition="top 0.2s"
        zIndex={10}
      >
        {isRefreshing ? (
          <Spinner color="blue.500" />
        ) : (
          <Box
            borderRadius="full"
            transform={`rotate(${(pullDistance / maxPull) * 360}deg)`}
            transition="color 0.3s"
            boxSizing="border-box"
            bgColor="white"
            boxShadow="lg"
            p={2}
            color={pullDistance >= maxPull ? 'blue.500' : 'gray.400'}
          >
            <FiRefreshCw size={27} />
          </Box>
        )}
      </Box>
      <Stack display={{ base: 'flex', lg: 'none' }} zIndex={1}>
        {data && emailSuport && (
          <AlertMobile title="Suporte ao Candidato" mx={0}>
            {emailSuport}
          </AlertMobile>
        )}
        <FilterSelectiveProcess
          onFinalSearch={setFinalSearch}
          selectedButton={selectedButton}
          onSelectedButton={setSelectedButton}
        />
      </Stack>

      <Text bg="primaryColor" fontSize="md" color="white" textAlign="center" py={2}>
        PROCESSOS SELETIVOS -{' '}
        <Text as="span" fontSize="md" fontWeight="bold">
          {labelFilter}
        </Text>
      </Text>

      {(isLoading && (
        <Stack h="full" align="center" justify="center" color="gray.400" gap={1}>
          <Spinner size="lg" />
          <Text fontSize="0.75rem">Carregando...</Text>
        </Stack>
      )) ||
        (data?.length === 0 && (
          <Stack flex={1} align="center" justify="center" color="gray.400" gap={1}>
            <ImFileEmpty size={30} />
            <Text>Sem processos seletivos ativos neste momento.</Text>
          </Stack>
        )) || (
          <Stack spacing={2}>
            {data?.map((selectiveProcess, idx) => (
              <CardSelectiveProcess key={selectiveProcess.id} data={selectiveProcess} index={idx} />
            ))}
          </Stack>
        )}
    </Stack>
  );
};

export default MobilePage;
